<template>
  <div class="screen-bill-admin-shell marketing-page">
    <v-container class="admin-container">
      <MarketingToolHeader
        eyebrow="Screen Marketing"
        title="Screen Bill"
        description="Werbeaufträge, Rechnungen und Ausspielung zentral organisieren und den gesamten Workflow im Blick behalten."
        icon="mdi-monitor-dashboard">
        <template v-slot:actions>
          <v-btn
            icon
            class="icon-action"
            @click="openBillingInitDialog"
            :loading="billingInitDialog.checking || billingInitDialog.applying"
            aria-label="Rechnungsdaten prüfen"
            title="Rechnungsdaten prüfen und initialisieren">
            <v-icon>mdi-database-check-outline</v-icon>
          </v-btn>
          <v-btn outlined color="#d7d7d8" class="secondary-action" @click="exportCSV" :loading="exporting" title="CSV Export">
            <v-icon left>mdi-file-delimited-outline</v-icon>
            CSV Export
          </v-btn>
          <v-btn icon class="icon-action" @click="fetchData" :loading="loading" aria-label="Aufträge aktualisieren" title="Aktualisieren">
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
          <v-btn color="#009640" depressed class="primary-action" @click="openCreateDialog" title="Auftrag erfassen">
            <v-icon left>mdi-plus</v-icon>
            Auftrag erfassen
          </v-btn>
        </template>
      </MarketingToolHeader>

      <section class="metric-grid" aria-label="Screen Bill Übersicht">
        <v-card dark flat class="metric-card">
          <span class="metric-icon icon-green"><v-icon>mdi-play-circle-outline</v-icon></span>
          <div>
            <span>Aufgeschaltet</span><strong>{{ loading ? "–" : stats.activeCount }}</strong
            ><small>aktuell im Sendedienst</small>
          </div>
        </v-card>
        <v-card dark flat class="metric-card">
          <span class="metric-icon icon-blue"><v-icon>mdi-cash-check</v-icon></span>
          <div>
            <span>Bezahlt</span><strong>{{ loading ? "–" : `CHF ${stats.paidVolume.toFixed(2)}` }}</strong
            ><small>beglichene Rechnungen</small>
          </div>
        </v-card>
        <v-card dark flat class="metric-card">
          <span class="metric-icon icon-amber"><v-icon>mdi-clock-alert-outline</v-icon></span>
          <div>
            <span>Ausstehend</span><strong>{{ loading ? "–" : stats.pendingCount }}</strong
            ><small>Zahlung noch offen</small>
          </div>
        </v-card>
        <v-card dark flat class="metric-card">
          <span class="metric-icon"><v-icon>mdi-archive-outline</v-icon></span>
          <div>
            <span>Deaktiviert</span><strong>{{ loading ? "–" : stats.deactivatedCount }}</strong
            ><small>abgelaufene Aufträge</small>
          </div>
        </v-card>
      </section>

      <v-card dark flat class="table-card filter-surface">
        <div class="surface-header">
          <div>
            <div class="surface-eyebrow">Schnellzugriff</div>
            <h2>Filter & Suche</h2>
            <p>Aufträge nach Kunde, Studio, Zeitraum oder Status eingrenzen.</p>
          </div>
          <span class="count-pill">{{ totalCount }} Aufträge</span>
        </div>
        <v-row dense align="center">
          <v-col cols="12" sm="4" md="3">
            <v-text-field
              v-model="filters.search"
              prepend-inner-icon="mdi-magnify"
              label="Kunde, Projekt oder Kontakt"
              single-line
              hide-details
              outlined
              class="search-field modern-field"
              @input="debouncedFetch"></v-text-field>
          </v-col>

          <v-col cols="12" sm="4" md="3">
            <v-select
              v-model="filters.studioId"
              :items="studios"
              item-text="name"
              item-value="_id"
              label="Standort"
              outlined
              hide-details
              clearable
              class="search-field modern-field"
              @change="fetchData"></v-select>
          </v-col>

          <v-col cols="12" sm="2" md="2">
            <v-text-field
              v-model="filters.startDate"
              label="Startzeitraum"
              type="date"
              outlined
              hide-details
              class="search-field modern-field"
              @change="fetchData"></v-text-field>
          </v-col>

          <v-col cols="12" sm="2" md="2">
            <v-text-field
              v-model="filters.endDate"
              label="Endzeitraum"
              type="date"
              outlined
              hide-details
              class="search-field modern-field"
              @change="fetchData"></v-text-field>
          </v-col>

          <v-col cols="12" sm="12" md="2" class="text-right">
            <v-btn text color="#b8b8bb" class="reset-action" @click="clearFilters"><v-icon left>mdi-filter-remove-outline</v-icon>Zurücksetzen</v-btn>
          </v-col>
        </v-row>

        <v-divider class="surface-divider"></v-divider>

        <v-chip-group v-model="filters.status" mandatory active-class="active-chip" class="status-filters" @change="fetchData">
          <v-chip filter value="all" outlined>Alle · {{ totalCount }}</v-chip>
          <v-chip filter value="active" outlined>Aufgeschaltet · {{ countWorkflowStatus("active") }}</v-chip>
          <v-chip filter value="paid" outlined>Bezahlt · {{ countWorkflowStatus("paid") }}</v-chip>
          <v-chip filter value="unpaid" outlined>Zahlung offen · {{ countWorkflowStatus("unpaid") }}</v-chip>
          <v-chip filter value="deactivated" outlined>Deaktiviert · {{ countWorkflowStatus("deactivated") }}</v-chip>
        </v-chip-group>
      </v-card>

      <v-card dark flat class="table-card order-surface">
        <div class="surface-header table-surface-header">
          <div>
            <div class="surface-eyebrow">Auftragsverwaltung</div>
            <h2>Werbeaufträge</h2>
            <p>Status, Ausspielung und Rechnungsdaten direkt bearbeiten.</p>
          </div>
          <span class="count-pill">{{ orders.length }} sichtbar</span>
        </div>
        <v-data-table
          :headers="headers"
          :items="orders"
          :loading="loading"
          class="custom-table"
          :items-per-page="15"
          no-data-text="Keine Werbeaufträge gefunden."
          dark>
          <!-- Company/Customer info -->
          <template v-slot:item.company="{ item }">
            <div class="py-1">
              <div class="body-2 font-weight-bold white--text">{{ item.companyName }}</div>
              <div class="caption grey--text d-flex flex-column">
                <span v-if="item.contactPerson">Ansprechp.: {{ item.contactPerson }}</span>
                <span>{{ item.street }}, {{ item.zip }} {{ item.city }}</span>
                <span v-if="item.createdBy" class="creator-label mt-1 font-weight-medium text-uppercase">
                  Erfasst von: {{ item.createdBy.vorname }} {{ item.createdBy.nachname }}
                </span>
              </div>
            </div>
          </template>

          <!-- Campaign / Period details -->
          <template v-slot:item.project="{ item }">
            <div class="py-1">
              <div class="body-2 font-weight-bold project-title">{{ item.projectTitle }}</div>
              <div class="caption grey--text mt-1">
                <v-icon x-small color="grey" class="mr-1">mdi-calendar-range</v-icon>
                {{ formatDate(item.startDate) }} - {{ formatDate(item.endDate) }}
              </div>
            </div>
          </template>

          <!-- Price -->
          <template v-slot:item.price="{ item }">
            <div class="font-weight-bold white--text">CHF {{ item.price.toFixed(2) }}</div>
            <div class="caption grey--text tax-label">inkl. 8.1% MWST</div>
          </template>

          <!-- Selected Studios -->
          <template v-slot:item.studios="{ item }">
            <div class="d-flex flex-wrap gap-1 max-w-studios">
              <v-chip v-for="studio in item.studios" :key="studio._id" x-small outlined color="grey lighten-1" class="ma-1">
                {{ studio.name }}
              </v-chip>
              <div v-if="!item.studios || !item.studios.length" class="caption grey--text italic">Keine</div>
            </div>
            <div v-if="item.invoiceIssuerStudio" class="caption issuer-label mt-1">Rechnungssteller: {{ item.invoiceIssuerStudio.name }}</div>
            <div v-else class="caption orange--text mt-1">Rechnungssteller fehlt</div>
          </template>

          <!-- Workflow Switches Checklist -->
          <template v-slot:item.workflow="{ item }">
            <div class="d-flex align-center justify-space-around py-1 workflow-box">
              <!-- Active status check -->
              <div class="d-flex flex-column align-center px-2">
                <span class="workflow-label">Aufgeschaltet</span>
                <v-checkbox v-model="item.isActive" color="green" hide-details dense @change="toggleWorkflowFlag(item, 'isActive')"></v-checkbox>
              </div>

              <!-- Paid status check -->
              <div class="d-flex flex-column align-center px-2">
                <span class="workflow-label">Bezahlt</span>
                <v-checkbox v-model="item.isPaid" color="green accent-3" hide-details dense @change="toggleWorkflowFlag(item, 'isPaid')"></v-checkbox>
              </div>

              <!-- Deactivated status check -->
              <div class="d-flex flex-column align-center px-2">
                <span class="workflow-label">Deaktiviert</span>
                <v-checkbox
                  v-model="item.isDeactivated"
                  color="red"
                  hide-details
                  dense
                  @change="toggleWorkflowFlag(item, 'isDeactivated')"></v-checkbox>
              </div>
            </div>
          </template>

          <!-- Zahlungszweck & Quick Edit -->
          <template v-slot:item.paymentPurpose="{ item }">
            <div class="d-flex align-center gap-2 py-1 max-w-purpose">
              <span class="caption text-truncate-purpose">{{ item.paymentPurpose }}</span>
              <v-btn icon x-small color="#58d388" @click="openPurposeQuickEdit(item)">
                <v-icon small>mdi-pencil-outline</v-icon>
              </v-btn>
            </div>
          </template>

          <!-- Action operations -->
          <template v-slot:item.actions="{ item }">
            <div class="d-flex align-center justify-end">
              <!-- Comments -->
              <v-btn icon color="blue lighten-2" class="mr-1" title="Kommentare & Notizen" @click="openCommentsDialog(item)">
                <v-badge
                  color="red"
                  :content="item.comments ? item.comments.length : 0"
                  :value="item.comments && item.comments.length > 0"
                  overlap
                  bordered
                  dot-class="caption">
                  <v-icon>mdi-comment-outline</v-icon>
                </v-badge>
              </v-btn>

              <!-- Export/Print PDF -->
              <v-btn icon color="green" class="mr-1" title="PDF Rechnungsbeleg herunterladen" @click="downloadPDF(item)">
                <v-icon>mdi-file-pdf-box</v-icon>
              </v-btn>

              <v-menu offset-y dark>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon v-bind="attrs" v-on="on">
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list dense bg-color="#1c1c1e">
                  <!-- Edit Details -->
                  <v-list-item @click="openEditDialog(item)">
                    <v-list-item-icon class="mr-2">
                      <v-icon color="#58d388" small>mdi-pencil</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>Bearbeiten</v-list-item-title>
                  </v-list-item>

                  <v-divider class="grey darken-3"></v-divider>

                  <!-- Delete Item -->
                  <v-list-item @click="confirmDelete(item)">
                    <v-list-item-icon class="mr-2">
                      <v-icon color="red" small>mdi-trash-can-outline</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title class="red--text">Löschen</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
          </template>
        </v-data-table>
      </v-card>
    </v-container>

    <!-- Create & Edit Campaign Dialog -->
    <v-dialog v-model="editDialog.show" max-width="700px" persistent scrollable :fullscreen="$vuetify.breakpoint.xsOnly">
      <v-card dark class="pa-4 rounded-xl border-highlight dialog-card order-dialog-card">
        <div class="dialog-header d-flex align-center justify-space-between mb-4">
          <h2 class="text-h5 font-weight-black white--text italic-title">
            <v-icon color="#58d388" class="mr-2">
              {{ editDialog.isNew ? "mdi-plus-box-outline" : "mdi-square-edit-outline" }}
            </v-icon>
            {{ editDialog.isNew ? "Neuen Werbeauftrag erfassen" : "Werbeauftrag bearbeiten" }}
          </h2>
          <v-btn icon color="white" @click="editDialog.show = false" :disabled="editDialog.saving">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <div class="dialog-scroll-body">
          <v-form ref="orderForm" v-model="editDialog.valid" lazy-validation>
            <!-- Customer Address Block -->
            <h3 class="subtitle-2 font-weight-black dialog-section-title text-uppercase mb-3">
              <v-icon color="#58d388" small class="mr-1">mdi-domain</v-icon>
              Kundendaten (Rechnungsadresse)
            </h3>
            <v-row dense>
              <v-col cols="12" sm="8">
                <v-text-field
                  v-model="editDialog.form.companyName"
                  label="Firmenname *"
                  outlined
                  dense
                  :rules="[(v) => !!v || 'Firmenname erforderlich']"
                  class="rounded-lg search-field"></v-text-field>
              </v-col>
              <v-col cols="12" sm="4">
                <v-text-field
                  v-model="editDialog.form.contactPerson"
                  label="Ansprechpartner (optional)"
                  outlined
                  dense
                  class="rounded-lg search-field"></v-text-field>
              </v-col>
              <v-col cols="12" sm="12">
                <v-text-field
                  v-model="editDialog.form.street"
                  label="Strasse / Hausnummer *"
                  outlined
                  dense
                  :rules="[(v) => !!v || 'Strasse erforderlich']"
                  class="rounded-lg search-field"></v-text-field>
              </v-col>
              <v-col cols="12" sm="4">
                <v-text-field
                  v-model="editDialog.form.zip"
                  label="PLZ *"
                  outlined
                  dense
                  :rules="[(v) => !!v || 'PLZ erforderlich']"
                  class="rounded-lg search-field"></v-text-field>
              </v-col>
              <v-col cols="12" sm="8">
                <v-text-field
                  v-model="editDialog.form.city"
                  label="Ort *"
                  outlined
                  dense
                  :rules="[(v) => !!v || 'Ort erforderlich']"
                  class="rounded-lg search-field"></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="editDialog.form.email"
                  label="E-Mail (optional)"
                  type="email"
                  outlined
                  dense
                  class="rounded-lg search-field"></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="editDialog.form.phone"
                  label="Telefonnummer (optional)"
                  outlined
                  dense
                  class="rounded-lg search-field"></v-text-field>
              </v-col>
            </v-row>

            <v-divider class="grey darken-3 my-4"></v-divider>

            <!-- Campaign / Billing Block -->
            <h3 class="subtitle-2 font-weight-black dialog-section-title text-uppercase mb-3">
              <v-icon color="#58d388" small class="mr-1">mdi-television-classic</v-icon>
              Werbekampagne & Preiskalkulation
            </h3>
            <v-row dense>
              <v-col cols="12" sm="8">
                <v-text-field
                  v-model="editDialog.form.projectTitle"
                  label="Projekt-/Kampagnenname *"
                  outlined
                  dense
                  :rules="[(v) => !!v || 'Projektname erforderlich']"
                  class="rounded-lg search-field"></v-text-field>
              </v-col>
              <v-col cols="12" sm="4">
                <v-text-field
                  v-model.number="editDialog.form.price"
                  label="Rechnungsbetrag (CHF) *"
                  type="number"
                  outlined
                  dense
                  min="0"
                  step="0.01"
                  prefix="CHF"
                  :rules="[(v) => (v !== undefined && v !== '' && v >= 0) || 'Betrag muss 0 oder höher sein']"
                  class="rounded-lg search-field"></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="editDialog.form.startDate"
                  label="Schaltung von *"
                  type="date"
                  outlined
                  dense
                  :rules="[(v) => !!v || 'Startdatum erforderlich']"
                  class="rounded-lg search-field"></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="editDialog.form.endDate"
                  label="Schaltung bis *"
                  type="date"
                  outlined
                  dense
                  :rules="[(v) => !!v || 'Enddatum erforderlich']"
                  class="rounded-lg search-field"></v-text-field>
              </v-col>

              <!-- Location Checklist multi-select -->
              <v-col cols="12">
                <span class="caption grey--text mb-2 d-block">Standorte auswählen (Studios) *</span>
                <div class="d-flex flex-wrap gap-2 studio-checkboxes-container pa-3 rounded-lg">
                  <v-checkbox
                    v-for="studio in studios"
                    :key="studio._id"
                    v-model="editDialog.form.studios"
                    :value="studio._id"
                    :label="studio.name"
                    color="#009640"
                    hide-details
                    class="ma-2 studio-cb-item"></v-checkbox>
                </div>
                <div v-if="!editDialog.form.studios.length" class="caption red--text mt-1">Bitte wählen Sie mindestens einen Standort aus.</div>
              </v-col>

              <v-col cols="12">
                <v-select
                  v-model="editDialog.form.invoiceIssuerStudio"
                  :items="invoiceIssuerOptions"
                  item-text="issuerLabel"
                  item-value="_id"
                  item-disabled="billingDisabled"
                  label="Rechnungssteller-Studio *"
                  outlined
                  dense
                  :disabled="!editDialog.form.studios.length"
                  :rules="[(v) => !!v || 'Rechnungssteller erforderlich']"
                  :hint="
                    editDialog.form.studios.length
                      ? 'Es können nur ausgewählte Standorte mit vollständig konfigurierten Rechnungsdaten verwendet werden.'
                      : 'Wählen Sie zuerst mindestens einen Standort aus.'
                  "
                  persistent-hint
                  no-data-text="Keine ausgewählten Standorte verfügbar"
                  class="rounded-lg search-field"></v-select>
              </v-col>

              <v-col cols="12" class="mt-2">
                <v-textarea
                  v-model="editDialog.form.description"
                  label="Projektbeschrieb / Detailinformationen (optional)"
                  outlined
                  dense
                  rows="3"
                  class="rounded-lg search-field"></v-textarea>
              </v-col>

              <!-- Editable Zahlungszweck (Only for updates or if they want to override manually) -->
              <v-col cols="12" v-if="!editDialog.isNew">
                <v-text-field
                  v-model="editDialog.form.paymentPurpose"
                  label="Zahlungszweck (Swiss QR-Bill Unstructured Message)"
                  outlined
                  dense
                  class="rounded-lg search-field"
                  placeholder="Automatisch generiert aus Zeitraum + Projektname"></v-text-field>
              </v-col>
            </v-row>
          </v-form>
        </div>

        <div class="dialog-actions d-flex justify-end gap-2 mt-6">
          <v-btn text color="white" @click="editDialog.show = false" :disabled="editDialog.saving"> Abbrechen </v-btn>
          <v-btn
            color="#009640"
            class="white--text font-weight-black rounded-lg"
            :loading="editDialog.saving"
            :disabled="!editDialog.valid || !editDialog.form.studios.length || !editDialog.form.invoiceIssuerStudio"
            @click="submitForm">
            {{ editDialog.isNew ? "Auftrag erstellen" : "Speichern" }}
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Studio billing migration dialog -->
    <v-dialog v-model="billingInitDialog.show" max-width="1180px" persistent scrollable :fullscreen="$vuetify.breakpoint.xsOnly">
      <v-card dark class="rounded-xl border-highlight dialog-card billing-dialog-card">
        <v-card-title class="dialog-header d-flex align-center justify-space-between">
          <span class="text-h6 font-weight-black white--text italic-title">
            <v-icon color="blue lighten-2" class="mr-2">mdi-database-eye-outline</v-icon>
            Rechnungsdaten kontrollieren
          </span>
          <v-btn icon color="white" @click="billingInitDialog.show = false" :disabled="billingInitDialog.checking || billingInitDialog.applying">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider class="grey darken-3"></v-divider>

        <v-card-text class="dialog-scroll-body pt-5">
          <div v-if="billingInitDialog.checking" class="py-10 text-center">
            <v-progress-circular indeterminate color="blue lighten-2" class="mb-3"></v-progress-circular>
            <div class="body-2 grey--text">Studio- und Rechnungsdaten werden geprüft…</div>
          </div>

          <v-alert v-else-if="billingInitDialog.error" type="error" text dense>
            {{ billingInitDialog.error }}
          </v-alert>

          <template v-else-if="billingInitDialog.result">
            <v-alert :type="billingInitDialog.applied ? 'success' : 'info'" text dense>
              {{ billingInitDialog.result.message }}
            </v-alert>

            <v-row dense class="mb-3">
              <v-col cols="12" sm="4">
                <v-card outlined dark class="pa-3 rounded-lg">
                  <div class="caption grey--text">Studio-Konfigurationen</div>
                  <div class="text-h6 font-weight-bold">{{ billingInitDialog.result.studioCount }}</div>
                </v-card>
              </v-col>
              <v-col cols="12" sm="4">
                <v-card outlined dark class="pa-3 rounded-lg">
                  <div class="caption grey--text">
                    {{ billingInitDialog.applied ? "Altaufträge ergänzt" : "Altaufträge würden ergänzt" }}
                  </div>
                  <div class="text-h6 font-weight-bold">{{ billingInitDialog.result.legacyOrdersUpdated }}</div>
                </v-card>
              </v-col>
              <v-col cols="12" sm="4">
                <v-card outlined dark class="pa-3 rounded-lg">
                  <div class="caption grey--text">Altaufträge manuell offen</div>
                  <div class="text-h6 font-weight-bold">{{ billingInitDialog.result.legacyOrdersPending }}</div>
                </v-card>
              </v-col>
            </v-row>

            <div class="d-flex flex-column flex-sm-row align-sm-center justify-space-between mb-3">
              <div>
                <div class="subtitle-1 font-weight-bold white--text">Billing-Daten nach Studio</div>
                <div class="caption grey--text">Diese Werte werden beim Ausführen im Studio-Modell gespeichert.</div>
              </div>
              <v-text-field
                v-model="billingInitDialog.search"
                append-icon="mdi-magnify"
                label="Studio oder Code suchen"
                outlined
                dense
                clearable
                hide-details
                class="search-field rounded-lg mt-3 mt-sm-0"
                style="max-width: 300px"></v-text-field>
            </div>

            <v-data-table
              :headers="billingInitHeaders"
              :items="billingInitDialog.result.studios || []"
              :search="billingInitDialog.search"
              item-key="code"
              :items-per-page="30"
              :footer-props="{ 'items-per-page-options': [15, 30, -1] }"
              dense
              dark
              class="custom-table rounded-lg"
              no-data-text="Keine Studio-Rechnungsdaten vorhanden.">
              <template v-slot:item.storedMatches="{ item }">
                <v-chip x-small outlined :color="item.storedMatches ? 'green' : 'orange'">
                  {{ item.storedMatches ? "Gespeichert" : "Wird aktualisiert" }}
                </v-chip>
              </template>
              <template v-slot:item.legalName="{ item }">
                <div class="font-weight-medium white--text">{{ item.legalName }}</div>
              </template>
              <template v-slot:item.qrIban="{ item }">
                <span class="text-no-wrap">{{ item.qrIban }}</span>
              </template>
              <template v-slot:item.billingAddress="{ item }">
                <div>{{ [item.street, item.buildingNumber].filter(Boolean).join(" ") }}</div>
                <div class="caption grey--text">{{ item.zip }} {{ item.city }}, {{ item.country }}</div>
                <div class="caption grey--text text--darken-1">Quelle: {{ item.sourceAddress }}</div>
              </template>
            </v-data-table>

            <div v-if="!billingInitDialog.applied" class="caption grey--text mt-3">
              Bitte gleiche insbesondere Code, Firma, QR-IBAN und MWST-Nummer mit dem Original-PDF ab. Erst danach «Migration ausführen» wählen.
            </div>
          </template>
        </v-card-text>

        <v-divider class="grey darken-3"></v-divider>

        <v-card-actions class="dialog-actions justify-end pa-4">
          <v-btn v-if="billingInitDialog.error" text color="blue lighten-2" :disabled="billingInitDialog.applying" @click="runBillingInitDryRun">
            Erneut prüfen
          </v-btn>
          <v-btn text color="white" :disabled="billingInitDialog.checking || billingInitDialog.applying" @click="billingInitDialog.show = false">
            {{ billingInitDialog.applied ? "Schliessen" : "Abbrechen" }}
          </v-btn>
          <v-btn
            v-if="billingInitDialog.result && !billingInitDialog.applied"
            color="blue darken-2"
            class="white--text font-weight-bold"
            :loading="billingInitDialog.applying"
            @click="applyBillingInitialization">
            Migration ausführen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Quick Edit Zahlungszweck Dialog -->
    <v-dialog v-model="purposeDialog.show" max-width="500px" scrollable :fullscreen="$vuetify.breakpoint.xsOnly">
      <v-card dark class="pa-4 rounded-xl border-highlight dialog-card compact-dialog-card">
        <div class="dialog-header d-flex align-center justify-space-between mb-4">
          <h2 class="text-h6 font-weight-black white--text italic-title">Zahlungszweck anpassen</h2>
          <v-btn icon color="white" @click="purposeDialog.show = false" :disabled="purposeDialog.saving">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <div class="dialog-scroll-body">
          <div class="caption grey--text mb-4">
            Passen Sie den Zahlungszweck an, der als unstrukturierte Mitteilung auf dem Schweizer QR-Beleg gedruckt wird.
          </div>

          <v-text-field v-model="purposeDialog.paymentPurpose" label="Zahlungszweck" outlined dense class="rounded-lg search-field"></v-text-field>
        </div>

        <div class="dialog-actions d-flex justify-end gap-2 mt-4">
          <v-btn text color="white" @click="purposeDialog.show = false" :disabled="purposeDialog.saving"> Abbrechen </v-btn>
          <v-btn color="#009640" class="white--text font-weight-black rounded-lg" :loading="purposeDialog.saving" @click="submitPurposeQuickEdit">
            Übernehmen
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Deletion Confirmation Dialog -->
    <v-dialog v-model="deleteDialog.show" max-width="400px" scrollable :fullscreen="$vuetify.breakpoint.xsOnly">
      <v-card dark class="pa-4 rounded-xl dialog-card compact-dialog-card">
        <h2 class="dialog-header text-h6 font-weight-black white--text mb-3">Werbeauftrag löschen?</h2>
        <div class="dialog-scroll-body">
          <p class="body-2 grey--text">
            Sind Sie sicher, dass Sie den Werbeauftrag für <strong>{{ deleteDialog.companyName }}</strong> ({{ deleteDialog.projectTitle }}) dauerhaft
            löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.
          </p>
        </div>
        <div class="dialog-actions d-flex justify-end gap-2 mt-4">
          <v-btn text color="white" @click="deleteDialog.show = false" :disabled="deleteDialog.saving"> Abbrechen </v-btn>
          <v-btn color="red" class="white--text font-weight-bold rounded-lg" :loading="deleteDialog.saving" @click="deleteOrder">
            Dauerhaft löschen
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Comments Dialog -->
    <v-dialog v-model="commentsDialog.show" max-width="500px" scrollable :fullscreen="$vuetify.breakpoint.xsOnly">
      <v-card dark class="pa-4 rounded-xl border-highlight dialog-card compact-dialog-card">
        <div class="dialog-header d-flex align-center justify-space-between mb-4">
          <h2 class="text-h6 font-weight-black white--text italic-title">
            <v-icon color="blue" class="mr-2">mdi-comment-text-multiple-outline</v-icon>
            Kommentare & Notizen
          </h2>
          <v-btn icon color="white" @click="commentsDialog.show = false" :disabled="commentsDialog.saving">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <div class="dialog-scroll-body">
          <div class="caption grey--text mb-4">
            Projekt: <strong class="white--text">{{ commentsDialog.projectTitle }}</strong> ({{ commentsDialog.companyName }})
          </div>

          <!-- Comments List -->
          <div
            class="comments-list-container pa-3 mb-4 rounded-lg"
            style="background-color: rgba(255, 255, 255, 0.02); max-height: 300px; overflow-y: auto">
            <div v-for="(comment, index) in commentsDialog.comments" :key="index" class="mb-3 pb-2 border-bottom-dashed">
              <div class="body-2 white--text mb-1" style="white-space: pre-line">{{ comment.text }}</div>
              <div class="comment-meta d-flex justify-space-between align-center">
                <span>{{ comment.user ? `${comment.user.vorname} ${comment.user.nachname}` : "System" }}</span>
                <span>{{ formatDateTime(comment.createdAt) }}</span>
              </div>
            </div>
            <div v-if="!commentsDialog.comments || !commentsDialog.comments.length" class="text-center py-6 caption grey--text italic">
              Keine Kommentare vorhanden. Erfassen Sie den ersten Kommentar unten.
            </div>
          </div>

          <!-- Add Comment form -->
          <v-textarea
            v-model="commentsDialog.newText"
            label="Neuer Kommentar..."
            outlined
            dense
            rows="2"
            hide-details
            class="rounded-lg search-field mb-3"></v-textarea>
        </div>

        <div class="dialog-actions d-flex justify-end gap-2">
          <v-btn text color="white" @click="commentsDialog.show = false" :disabled="commentsDialog.saving"> Schliessen </v-btn>
          <v-btn
            color="#009640"
            class="white--text font-weight-black rounded-lg"
            :loading="commentsDialog.saving"
            :disabled="!commentsDialog.newText || !commentsDialog.newText.trim()"
            @click="submitComment">
            Kommentar senden
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import api from "@/api"
import MarketingToolHeader from "../MarketingToolHeader.vue"

export default {
  name: "ScreenBillAdmin",
  components: { MarketingToolHeader },
  data() {
    return {
      loading: false,
      exporting: false,
      totalCount: 0,

      // Filter inputs
      filters: {
        search: "",
        status: "all",
        studioId: null,
        startDate: "",
        endDate: "",
      },
      debounceTimer: null,

      // Database lists
      orders: [],
      studios: [],

      // Stats counters
      stats: {
        activeCount: 0,
        paidVolume: 0,
        pendingCount: 0,
        deactivatedCount: 0,
      },

      // Add/Edit Dialog data
      editDialog: {
        show: false,
        isNew: true,
        valid: true,
        saving: false,
        orderId: null,
        form: {
          companyName: "",
          contactPerson: "",
          street: "",
          zip: "",
          city: "",
          email: "",
          phone: "",
          startDate: "",
          endDate: "",
          projectTitle: "",
          description: "",
          price: 0,
          studios: [],
          invoiceIssuerStudio: null,
          paymentPurpose: "",
        },
      },

      // Studio billing migration dialog
      billingInitDialog: {
        show: false,
        checking: false,
        applying: false,
        applied: false,
        result: null,
        error: "",
        search: "",
      },

      billingInitHeaders: [
        { text: "Status", value: "storedMatches", sortable: false, width: "120px" },
        { text: "Studio", value: "studioName" },
        { text: "Code", value: "code" },
        { text: "Rechnungssteller", value: "legalName" },
        { text: "QR-IBAN", value: "qrIban", sortable: false },
        { text: "MWST-Nr.", value: "vatNumber" },
        { text: "Billing-Adresse", value: "billingAddress", sortable: false, width: "230px" },
      ],

      // Quick Purpose Dialog data
      purposeDialog: {
        show: false,
        saving: false,
        orderId: null,
        paymentPurpose: "",
      },

      // Deletion Dialog data
      deleteDialog: {
        show: false,
        saving: false,
        orderId: null,
        companyName: "",
        projectTitle: "",
      },

      // Comments Dialog data
      commentsDialog: {
        show: false,
        saving: false,
        orderId: null,
        companyName: "",
        projectTitle: "",
        comments: [],
        newText: "",
      },

      // Table headers
      headers: [
        { text: "Kunde / Rechnungsadresse", value: "company", sortable: false },
        { text: "Projekt & Zeitraum", value: "project", sortable: false },
        { text: "Preis", value: "price", align: "right", width: "120px" },
        { text: "Standorte", value: "studios", sortable: false, width: "180px" },
        { text: "Workflow-Schritte", value: "workflow", align: "center", sortable: false, width: "240px" },
        { text: "Zahlungszweck (QR)", value: "paymentPurpose", sortable: false, width: "200px" },
        { text: "", value: "actions", sortable: false, align: "right", width: "100px" },
      ],
    }
  },
  computed: {
    invoiceIssuerOptions() {
      const selectedStudioIds = new Set((this.editDialog.form.studios || []).map((studioId) => studioId.toString()))

      return this.studios
        .filter((studio) => selectedStudioIds.has(studio._id.toString()))
        .map((studio) => ({
          ...studio,
          issuerLabel: studio.billingConfigured ? studio.name : `${studio.name} (Rechnungsdaten fehlen)`,
          billingDisabled: !studio.billingConfigured,
        }))
    },
  },
  watch: {
    "editDialog.form.studios"(studioIds) {
      const invoiceIssuerStudio = this.editDialog.form.invoiceIssuerStudio
      if (!invoiceIssuerStudio) return

      const issuerStillSelected = (studioIds || []).some((studioId) => studioId.toString() === invoiceIssuerStudio.toString())
      if (!issuerStillSelected) {
        this.editDialog.form.invoiceIssuerStudio = null
      }
    },
  },
  async created() {
    this.loading = true
    await this.fetchStudios()
    await this.fetchData()
  },
  methods: {
    formatDate(dateStr) {
      if (!dateStr) return ""
      const d = new Date(dateStr)
      if (isNaN(d.getTime())) return ""
      return d.toLocaleDateString("de-CH", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    },

    formatInputDate(dateStr) {
      if (!dateStr) return ""
      const d = new Date(dateStr)
      if (isNaN(d.getTime())) return ""
      const year = d.getFullYear()
      const month = String(d.getMonth() + 1).padStart(2, "0")
      const day = String(d.getDate()).padStart(2, "0")
      return `${year}-${month}-${day}`
    },

    debouncedFetch() {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = setTimeout(() => {
        this.fetchData()
      }, 350)
    },

    clearFilters() {
      this.filters = {
        search: "",
        status: "all",
        studioId: null,
        startDate: "",
        endDate: "",
      }
      this.fetchData()
    },

    countWorkflowStatus(status) {
      if (status === "active") {
        return this.orders.filter((o) => o.isActive && !o.isDeactivated).length
      } else if (status === "paid") {
        return this.orders.filter((o) => o.isPaid).length
      } else if (status === "unpaid") {
        return this.orders.filter((o) => !o.isPaid && !o.isDeactivated).length
      } else if (status === "deactivated") {
        return this.orders.filter((o) => o.isDeactivated).length
      }
      return 0
    },

    async fetchStudios() {
      try {
        const res = await api.screenBill.admin.fetchStudios()
        this.studios = res || []
      } catch (err) {
        console.error(err)
        this.$toast.error("Standorte konnten nicht geladen werden.")
      }
    },

    openBillingInitDialog() {
      this.billingInitDialog = {
        show: true,
        checking: false,
        applying: false,
        applied: false,
        result: null,
        error: "",
        search: "",
      }
      this.runBillingInitDryRun()
    },

    async runBillingInitDryRun() {
      this.billingInitDialog.checking = true
      this.billingInitDialog.applied = false
      this.billingInitDialog.result = null
      this.billingInitDialog.error = ""

      try {
        this.billingInitDialog.result = await api.screenBill.admin.initBilling(false)
      } catch (err) {
        console.error(err)
        this.billingInitDialog.error = err.response?.data || "Die Rechnungsdaten konnten nicht geprüft werden."
      } finally {
        this.billingInitDialog.checking = false
      }
    },

    async applyBillingInitialization() {
      this.billingInitDialog.applying = true
      this.billingInitDialog.error = ""

      try {
        this.billingInitDialog.result = await api.screenBill.admin.initBilling(true)
        this.billingInitDialog.applied = true
        this.$toast.success("Rechnungsdaten erfolgreich initialisiert.")
        await this.fetchStudios()
        await this.fetchData()
      } catch (err) {
        console.error(err)
        this.billingInitDialog.error = err.response?.data || "Die Rechnungsdaten konnten nicht initialisiert werden."
      } finally {
        this.billingInitDialog.applying = false
      }
    },

    async fetchData() {
      this.loading = true
      try {
        const params = {
          search: this.filters.search,
          studioId: this.filters.studioId,
          startDate: this.filters.startDate,
          endDate: this.filters.endDate,
        }

        if (this.filters.status !== "all") {
          params.status = this.filters.status
        }

        const res = await api.screenBill.admin.fetchOrders(params)
        this.orders = res || []
        this.totalCount = this.orders.length
        this.recalculateStats()
      } catch (err) {
        console.error(err)
        this.$toast.error("Fehler beim Laden der Werbeaufträge.")
      } finally {
        this.loading = false
      }
    },

    recalculateStats() {
      let activeCount = 0
      let paidVolume = 0
      let pendingCount = 0
      let deactivatedCount = 0

      this.orders.forEach((o) => {
        // Count revenue for any paid order, active or deactivated
        if (o.isPaid) {
          paidVolume += o.price
        }

        // Count active screen campaigns
        if (o.isActive && !o.isDeactivated) {
          activeCount++
        }

        // Count deactivated orders
        if (o.isDeactivated) {
          deactivatedCount++
        }

        // Count pending unpaid orders (neither paid nor deactivated)
        if (!o.isPaid && !o.isDeactivated) {
          pendingCount++
        }
      })

      this.stats = {
        activeCount,
        paidVolume,
        pendingCount,
        deactivatedCount,
      }
    },

    openCreateDialog() {
      this.editDialog = {
        show: true,
        isNew: true,
        valid: true,
        saving: false,
        orderId: null,
        form: {
          companyName: "",
          contactPerson: "",
          street: "",
          zip: "",
          city: "",
          email: "",
          phone: "",
          startDate: this.formatInputDate(new Date()),
          endDate: this.formatInputDate(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)), // 30 days default
          projectTitle: "",
          description: "",
          price: 0,
          studios: [],
          invoiceIssuerStudio: null,
          paymentPurpose: "",
        },
      }
      this.$nextTick(() => {
        if (this.$refs.orderForm) this.$refs.orderForm.resetValidation()
      })
    },

    openEditDialog(item) {
      this.editDialog = {
        show: true,
        isNew: false,
        valid: true,
        saving: false,
        orderId: item._id,
        form: {
          companyName: item.companyName,
          contactPerson: item.contactPerson || "",
          street: item.street,
          zip: item.zip,
          city: item.city,
          email: item.email || "",
          phone: item.phone || "",
          startDate: this.formatInputDate(item.startDate),
          endDate: this.formatInputDate(item.endDate),
          projectTitle: item.projectTitle,
          description: item.description || "",
          price: item.price,
          studios: (item.studios || []).map((s) => s._id || s),
          invoiceIssuerStudio: item.invoiceIssuerStudio ? item.invoiceIssuerStudio._id || item.invoiceIssuerStudio : null,
          paymentPurpose: item.paymentPurpose || "",
        },
      }
      this.$nextTick(() => {
        if (this.$refs.orderForm) this.$refs.orderForm.resetValidation()
      })
    },

    async submitForm() {
      if (this.$refs.orderForm && !this.$refs.orderForm.validate()) {
        return
      }

      if (!this.editDialog.form.studios.length || !this.editDialog.form.invoiceIssuerStudio) {
        this.$toast.error("Bitte wählen Sie Standorte und einen Rechnungssteller aus.")
        return
      }

      this.editDialog.saving = true
      try {
        if (this.editDialog.isNew) {
          await api.screenBill.admin.createOrder(this.editDialog.form)
          this.$toast.success("Werbeauftrag erfolgreich erfasst.")
        } else {
          await api.screenBill.admin.updateOrder(this.editDialog.orderId, this.editDialog.form)
          this.$toast.success("Werbeauftrag erfolgreich aktualisiert.")
        }
        this.editDialog.show = false
        await this.fetchData()
      } catch (err) {
        console.error(err)
        this.$toast.error(err.response?.data || "Speichern fehlgeschlagen.")
      } finally {
        this.editDialog.saving = false
      }
    },

    async toggleWorkflowFlag(item, flagName) {
      try {
        const payload = {}
        payload[flagName] = item[flagName]

        // Automatisches Anpassen der anderen Flags für intuitive Bedienung:
        // z.B. wenn deaktiviert wird, wird active ausgeschaltet (oder umgekehrt)
        if (flagName === "isDeactivated" && item.isDeactivated) {
          item.isActive = false
          payload.isActive = false
        } else if (flagName === "isActive" && item.isActive) {
          item.isDeactivated = false
          payload.isDeactivated = false
        }

        await api.screenBill.admin.updateOrder(item._id, payload)
        this.$toast.success("Workflow-Status aktualisiert.")
        this.recalculateStats()
      } catch (err) {
        console.error(err)
        this.$toast.error("Statusaktualisierung fehlgeschlagen.")
        // Rollback state visually
        item[flagName] = !item[flagName]
      }
    },

    openPurposeQuickEdit(item) {
      this.purposeDialog = {
        show: true,
        saving: false,
        orderId: item._id,
        paymentPurpose: item.paymentPurpose || "",
      }
    },

    async submitPurposeQuickEdit() {
      this.purposeDialog.saving = true
      try {
        await api.screenBill.admin.updateOrder(this.purposeDialog.orderId, {
          paymentPurpose: this.purposeDialog.paymentPurpose,
        })
        this.$toast.success("Zahlungszweck erfolgreich geändert.")
        this.purposeDialog.show = false
        await this.fetchData()
      } catch (err) {
        console.error(err)
        this.$toast.error("Ändern des Zahlungszwecks fehlgeschlagen.")
      } finally {
        this.purposeDialog.saving = false
      }
    },

    confirmDelete(item) {
      this.deleteDialog = {
        show: true,
        saving: false,
        orderId: item._id,
        companyName: item.companyName,
        projectTitle: item.projectTitle,
      }
    },

    async deleteOrder() {
      this.deleteDialog.saving = true
      try {
        await api.screenBill.admin.deleteOrder(this.deleteDialog.orderId)
        this.$toast.success("Werbeauftrag erfolgreich gelöscht.")
        this.deleteDialog.show = false
        await this.fetchData()
      } catch (err) {
        console.error(err)
        this.$toast.error("Löschen fehlgeschlagen.")
      } finally {
        this.deleteDialog.saving = false
      }
    },

    downloadPDF(item) {
      try {
        const filename = `Rechnung_${item.companyName.replace(/\s+/g, "_")}.pdf`
        this.$toast.info("PDF wird generiert...")
        api.screenBill.admin.downloadPDF(item._id, filename).catch((err) => {
          console.error(err)
          this.$toast.error("Fehler beim Herunterladen des PDF-Belegs.")
        })
      } catch (error) {
        console.error(error)
        this.$toast.error("PDF-Export fehlgeschlagen.")
      }
    },

    exportCSV() {
      this.exporting = true
      try {
        api.screenBill.admin
          .downloadCSV("screen_bill_auftraege.csv")
          .then(() => {
            this.$toast.success("CSV Export erfolgreich.")
          })
          .catch((err) => {
            console.error(err)
            this.$toast.error("CSV Export fehlgeschlagen.")
          })
          .finally(() => {
            this.exporting = false
          })
      } catch (error) {
        this.exporting = false
        console.error(error)
        this.$toast.error("CSV Export fehlgeschlagen.")
      }
    },

    openCommentsDialog(item) {
      this.commentsDialog = {
        show: true,
        saving: false,
        orderId: item._id,
        companyName: item.companyName,
        projectTitle: item.projectTitle,
        comments: item.comments || [],
        newText: "",
      }
    },

    async submitComment() {
      if (!this.commentsDialog.newText || !this.commentsDialog.newText.trim()) return

      this.commentsDialog.saving = true
      try {
        const updatedOrder = await api.screenBill.admin.addComment(this.commentsDialog.orderId, this.commentsDialog.newText)
        this.$toast.success("Kommentar erfolgreich hinzugefügt.")

        this.commentsDialog.comments = updatedOrder.comments || []
        this.commentsDialog.newText = ""

        const idx = this.orders.findIndex((o) => o._id === this.commentsDialog.orderId)
        if (idx !== -1) {
          this.$set(this.orders, idx, updatedOrder)
        }
      } catch (err) {
        console.error(err)
        this.$toast.error("Kommentar konnte nicht gespeichert werden.")
      } finally {
        this.commentsDialog.saving = false
      }
    },

    formatDateTime(dateStr) {
      if (!dateStr) return ""
      const d = new Date(dateStr)
      if (isNaN(d.getTime())) return ""
      return d.toLocaleString("de-CH", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    },
  },
}
</script>

<style scoped>
.marketing-page {
  --surface: rgba(255, 255, 255, 0.035);
  --surface-strong: rgba(255, 255, 255, 0.055);
  --border: rgba(255, 255, 255, 0.1);
  --border-strong: rgba(255, 255, 255, 0.16);
  --text: #f4f4f5;
  --text-soft: #d4d4d8;
  --muted: #a3a3a8;
}

.screen-bill-admin-shell {
  min-height: 100vh;
  padding: 1.5rem;
  color: var(--text);
  background: radial-gradient(circle at 8% 0%, rgba(0, 150, 64, 0.075), transparent 24rem), #0f0f10;
  font-size: 1rem;
}

.admin-container {
  max-width: 92.5rem !important;
  margin: 0 auto;
  padding: 0 !important;
}

.italic-title {
  font-style: normal;
  font-weight: 800;
  letter-spacing: -0.025em;
}

.icon-action {
  width: 2.75rem !important;
  min-width: 2.75rem !important;
  height: 2.75rem !important;
  border: 1px solid var(--border);
  border-radius: 0.8125rem !important;
  color: #ffffff !important;
  background: rgba(255, 255, 255, 0.055) !important;
}

.primary-action,
.secondary-action,
.reset-action {
  min-height: 2.75rem !important;
  border-radius: 0.8125rem !important;
  font-size: 0.875rem !important;
  font-weight: 750;
  letter-spacing: 0;
  text-transform: none;
}

.primary-action {
  color: #ffffff !important;
}

.metric-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem;
  margin: 1rem 0;
}

.metric-card {
  display: flex;
  min-width: 0;
  min-height: 6.75rem;
  align-items: center;
  gap: 0.875rem;
  padding: 1.125rem;
  border: 1px solid var(--border);
  border-radius: 1.1875rem !important;
  background: var(--surface) !important;
  box-shadow: 0 0.75rem 1.875rem rgba(0, 0, 0, 0.1);
}

.metric-icon {
  display: grid;
  flex: 0 0 auto;
  width: 2.625rem;
  height: 2.625rem;
  place-items: center;
  border-radius: 0.8125rem;
  color: #c5c5c8;
  background: rgba(255, 255, 255, 0.065);
}

.icon-green {
  color: #58d388;
  background: rgba(0, 150, 64, 0.12);
}

.icon-blue {
  color: #7dd3fc;
  background: rgba(14, 165, 233, 0.12);
}

.icon-amber {
  color: #fcd34d;
  background: rgba(245, 158, 11, 0.12);
}

.metric-card > div {
  display: grid;
  min-width: 0;
}

.metric-card span,
.metric-card small {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.metric-card > div > span {
  color: var(--text-soft);
  font-size: 0.875rem;
  font-weight: 700;
}

.metric-card strong {
  overflow: hidden;
  color: #ffffff;
  font-size: clamp(1.25rem, 2vw, 1.6875rem);
  line-height: 1.15;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.metric-card small {
  margin-top: 0.1875rem;
  color: var(--muted);
  font-size: 0.75rem;
}

.table-card {
  padding: 1.375rem;
  overflow: hidden;
  border: 1px solid var(--border) !important;
  border-radius: 1.375rem !important;
  background: rgba(255, 255, 255, 0.03) !important;
  box-shadow: 0 1rem 2.75rem rgba(0, 0, 0, 0.12) !important;
}

.order-surface {
  margin-top: 1rem;
}

.surface-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.125rem;
  margin-bottom: 1.125rem;
}

.surface-eyebrow {
  color: #58d388;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.surface-header h2 {
  margin: 0.1875rem 0 0;
  color: var(--text);
  font-size: 1.25rem;
  line-height: 1.25;
}

.surface-header p {
  margin: 0.25rem 0 0;
  color: var(--muted);
  font-size: 0.875rem;
  line-height: 1.5;
}

.count-pill {
  display: inline-flex;
  min-height: 1.75rem;
  align-items: center;
  padding: 0.25rem 0.625rem;
  border: 1px solid var(--border);
  border-radius: 999rem;
  color: var(--text-soft);
  background: rgba(255, 255, 255, 0.055);
  font-size: 0.75rem;
  font-weight: 700;
  white-space: nowrap;
}

.surface-divider {
  margin: 1rem 0 !important;
  border-color: var(--border) !important;
}

.modern-field ::v-deep .v-input__slot,
.search-field ::v-deep .v-input__slot {
  min-height: 3rem !important;
  border-radius: 0.875rem !important;
  background: rgba(255, 255, 255, 0.025) !important;
}

.search-field ::v-deep .v-label,
.search-field ::v-deep input,
.search-field ::v-deep textarea,
.search-field ::v-deep .v-select__selection {
  font-size: 0.875rem !important;
}

.status-filters ::v-deep .v-chip {
  min-height: 2rem;
  border-color: var(--border) !important;
  color: #c8c8cc !important;
  background: rgba(255, 255, 255, 0.025) !important;
  font-size: 0.75rem !important;
}

.status-filters ::v-deep .active-chip {
  border-color: rgba(0, 150, 64, 0.5) !important;
  color: #7ce2a5 !important;
  background: rgba(0, 150, 64, 0.14) !important;
}

.custom-table {
  border: 1px solid rgba(255, 255, 255, 0.075);
  border-radius: 1rem;
  background: rgba(0, 0, 0, 0.08) !important;
}

.custom-table ::v-deep .v-data-table__wrapper {
  overflow-x: auto;
}

.custom-table ::v-deep table {
  min-width: 82rem;
  background: transparent !important;
}

.custom-table ::v-deep thead th {
  height: 3.25rem;
  border-bottom: 1px solid var(--border) !important;
  color: #929297 !important;
  font-size: 0.75rem !important;
  font-weight: 800 !important;
  letter-spacing: 0.055em;
  text-transform: uppercase;
}

.custom-table ::v-deep tbody td {
  min-height: 4.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.055) !important;
  font-size: 0.875rem;
}

.custom-table ::v-deep tbody tr:hover {
  background: rgba(255, 255, 255, 0.035) !important;
}

.custom-table ::v-deep .v-data-footer,
.custom-table ::v-deep .v-data-footer__select {
  font-size: 0.875rem;
}

.creator-label,
.tax-label,
.workflow-label,
.comment-meta {
  font-size: 0.75rem !important;
}

.creator-label,
.project-title,
.issuer-label,
.dialog-section-title {
  color: #58d388 !important;
}

.workflow-box {
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 0.75rem;
  background: rgba(255, 255, 255, 0.025);
}

.workflow-label {
  color: #929297;
  font-weight: 700;
  letter-spacing: 0.035em;
  text-transform: uppercase;
}

.max-w-studios {
  max-width: 11.25rem;
}

.max-w-purpose {
  max-width: 12.5rem;
}

.text-truncate-purpose {
  display: inline-block;
  max-width: 10rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dialog-card {
  --surface: rgba(255, 255, 255, 0.035);
  --surface-strong: rgba(255, 255, 255, 0.055);
  --border: rgba(255, 255, 255, 0.1);
  --border-strong: rgba(255, 255, 255, 0.16);
  --text: #f4f4f5;
  --text-soft: #d4d4d8;
  --muted: #a3a3a8;
  width: 100%;
  max-height: calc(100vh - 2rem);
  max-height: calc(100dvh - 2rem);
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.12) !important;
  color: var(--text) !important;
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.025), transparent 40%), #1b1b1d !important;
  box-shadow: 0 1.75rem 5rem rgba(0, 0, 0, 0.42) !important;
  font-size: 1rem;
}

.order-dialog-card,
.billing-dialog-card,
.compact-dialog-card {
  display: flex;
  min-height: 0;
  flex-direction: column;
}

.dialog-header,
.dialog-actions {
  flex: 0 0 auto;
}

.dialog-scroll-body {
  min-height: 0;
  flex: 1 1 auto;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
}

.order-dialog-card > .dialog-scroll-body,
.compact-dialog-card > .dialog-scroll-body {
  padding-right: 0.25rem;
}

.dialog-actions {
  z-index: 1;
  flex-wrap: wrap;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  background: #1b1b1d;
}

.order-dialog-card > .dialog-actions,
.compact-dialog-card > .dialog-actions {
  padding-top: 1rem;
}

.dialog-scroll-body::-webkit-scrollbar {
  width: 0.375rem;
}

.dialog-scroll-body::-webkit-scrollbar-thumb {
  border-radius: 999rem;
  background: rgba(255, 255, 255, 0.14);
}

.dialog-section-title {
  font-size: 0.75rem !important;
  letter-spacing: 0.095em;
}

.studio-checkboxes-container {
  max-height: 12rem;
  overflow-y: auto;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.025);
}

.studio-cb-item {
  width: 40%;
  min-width: 7.5rem;
}

.comment-meta {
  color: #8f9095;
}

.comments-list-container {
  background: rgba(255, 255, 255, 0.025) !important;
}

.gap-1 {
  gap: 0.25rem;
}

.gap-2 {
  gap: 0.5rem;
}

.gap-3 {
  gap: 0.75rem;
}

.border-bottom-dashed {
  border-bottom: 1px dashed rgba(255, 255, 255, 0.08);
}

.comments-list-container::-webkit-scrollbar {
  width: 0.375rem;
}

.comments-list-container::-webkit-scrollbar-thumb {
  border-radius: 999rem;
  background: rgba(255, 255, 255, 0.12);
}

@media (max-width: 74.9375rem) {
  .metric-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 59.9375rem) {
  .screen-bill-admin-shell {
    padding: 1rem;
  }

  .table-card {
    padding: 1.125rem;
  }

  .table-surface-header {
    align-items: flex-start;
  }
}

@media (max-width: 37.4375rem) {
  .order-dialog-card,
  .billing-dialog-card,
  .compact-dialog-card {
    height: 100vh;
    height: 100dvh;
    max-height: 100vh;
    max-height: 100dvh;
    border: 0 !important;
    border-radius: 0 !important;
  }

  .dialog-header {
    align-items: flex-start !important;
  }

  .dialog-actions {
    padding-bottom: max(0.75rem, env(safe-area-inset-bottom)) !important;
  }

  .order-dialog-card > .dialog-actions,
  .compact-dialog-card > .dialog-actions {
    margin-top: 0.75rem !important;
  }

  .screen-bill-admin-shell {
    padding: 0.75rem;
  }

  .metric-grid {
    grid-template-columns: 1fr;
  }

  .metric-card {
    min-height: 6rem;
  }

  .surface-header {
    align-items: flex-start;
    flex-direction: column;
    gap: 0.75rem;
  }

  .count-pill {
    align-self: flex-start;
  }

  .filter-surface {
    padding: 1rem;
  }

  .order-surface {
    padding: 1rem 0 0;
  }

  .order-surface .surface-header {
    padding: 0 1rem;
  }

  .reset-action {
    width: 100%;
  }

  .studio-cb-item {
    width: 100%;
  }
}
</style>
